import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Spinner, Table, Badge } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import { formatBytes } from '../../helpers/formatHelpers';

export const AdminGeneratedPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.listGeneratedPackages();
      setPackages(data || []);
    } catch (exc) {
      console.error('Failed to load packages:', exc);
      setError('Failed to load packages: ' + exc.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const handleTrigger = async () => {
    if (!window.confirm('Trigger CTRAC package generation now? This may take several minutes.')) return;
    try {
      setTriggerLoading(true);
      setError(null);
      setSuccess(null);
      await apiClient.triggerPackageGeneration();
      setSuccess('Package generation has been queued. The package will appear in the list once complete.');
    } catch (exc) {
      console.error('Failed to trigger generation:', exc);
      setError('Failed to trigger generation: ' + exc.message);
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleDownload = (pkg) => {
    apiClient.downloadGeneratedPackage(pkg.Name);
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Generated CTRAC Packages</h4>
        <div className="d-flex gap-2">
          <Button color="secondary" outline size="sm" onClick={loadPackages} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Refresh'}
          </Button>
          <Button color="primary" size="sm" onClick={handleTrigger} disabled={triggerLoading}>
            {triggerLoading ? (
              <>
                <Spinner size="sm" className="me-1" /> Queuing…
              </>
            ) : (
              'Generate Now'
            )}
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="card">
        <div className="card-body p-0">
          {loading && packages.length === 0 ? (
            <div className="text-center py-4">
              <Spinner />
              <p className="mt-2 text-muted">Loading packages…</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No packages generated yet.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>File Name</th>
                  <th>Size</th>
                  <th>Generated At</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, idx) => (
                  <tr key={pkg.Name}>
                    <td>
                      {pkg.Name}
                      {idx === 0 && (
                        <Badge color="success" className="ms-2">
                          Latest
                        </Badge>
                      )}
                    </td>
                    <td>{formatBytes(pkg.SizeBytes)}</td>
                    <td>{pkg.getFormattedCreatedAt()}</td>
                    <td>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handleDownload(pkg)}
                      >
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <p className="text-muted mt-3" style={{ fontSize: '0.85rem' }}>
        Packages are generated automatically Tuesday–Saturday at 05:00 UTC. The folder keeps up to
        10 packages; older ones are removed automatically.
      </p>
    </div>
  );
};
